import axiosInstance from 'services/axiosInstance';
import { IResponse } from 'services/types';
import { CallScribeCallStatus } from 'services/callScribe/types';
import { sessionToken } from 'utils/session';

/**
 * Body for `PATCH /api/call-scribe/calls/{id}/lifecycle`.
 *
 * Mirrors `AlertManager.Models.CallScribe.CallScribeLifecycleUpdate` on the BE.
 * Every field is optional — the server merges into the existing row using
 * forward-only state semantics (status never regresses, non-null timestamps
 * never overwritten). The Twilio status callback is authoritative for
 * terminal state; this PATCH is the low-latency path so the UI reflects the
 * hangup before the carrier callback lands.
 */
export interface ICallScribeLifecycleUpdate {
  status?: CallScribeCallStatus;
  startedAtUtc?: string;
  answeredAtUtc?: string;
  endedAtUtc?: string;
  durationSeconds?: number;
}

/**
 * Sends a lifecycle PATCH for the given call id. Errors are swallowed at the
 * call-site with a console warning — a missed PATCH is not user-actionable
 * (the BE will eventually receive the Twilio status callback), and surfacing
 * a snackbar mid-call would be more harmful than the missed write.
 *
 * Terminal updates (any body carrying `endedAtUtc`) are delivered with
 * `fetch(…, { keepalive: true })` instead of axios: the popup calls
 * `window.close()` immediately after the disconnect handler fires, and a
 * keepalive request survives document teardown where an XHR is aborted.
 *
 * @param callId  CallScribeCall.Id returned by the Initiate POST.
 * @param body    Partial lifecycle update; omit any field that has not changed.
 */
export const patchCallScribeLifecycle = (
  callId: number,
  body: ICallScribeLifecycleUpdate,
): IResponse<void> => {
  if (body.endedAtUtc !== undefined && typeof fetch === 'function') {
    const base = String(axiosInstance.defaults.baseURL ?? '');
    const url = `${base.replace(/\/+$/, '')}/call-scribe/calls/${callId}/lifecycle`;
    const accessToken = sessionToken();
    return fetch(url, {
      method: 'PATCH',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(body),
    }) as unknown as IResponse<void>;
  }
  return axiosInstance.request({
    method: 'PATCH',
    url: `call-scribe/calls/${callId}/lifecycle`,
    data: body,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    skipErrorSnackbar: true,
  });
};
